import { ArrowUpRight, Wind, Zap, FileDown } from "lucide-react";
import { PRODUCTS, SPECS } from "./data";
import { Reveal, scrollToId } from "./motion";

const ProductCard = ({ p, i }) => (
  <Reveal delay={i * 0.08} className={p.span}>
    <div
      data-testid={`product-card-${p.id}`}
      role="button"
      tabIndex={0}
      onClick={() => scrollToId("quote")}
      onKeyDown={(e) => e.key === "Enter" && scrollToId("quote")}
      className="group relative w-full text-left cursor-pointer rounded-3xl overflow-hidden border border-[#262626] bg-[#0d0d0d] hover:border-[#fcc41b]/40 transition-colors duration-500 h-full min-h-[440px] flex flex-col"
    >
      {/* Studio image panel */}
      <div className="relative h-64 lg:h-72 overflow-hidden flex items-center justify-center bg-gradient-to-br from-[#f4f4f5] to-[#d4d4d8]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(252,196,27,0.25),transparent_60%)]" />
        <img
          src={p.image}
          alt={`SolarArk ${p.name} solar roof ventilator`}
          className={`relative z-10 max-h-[85%] max-w-[85%] object-contain drop-shadow-2xl group-hover:scale-105 transition-transform duration-700 ease-out ${p.blend ? "mix-blend-multiply" : ""}`}
        />
        <span className="absolute top-5 left-5 z-20 font-mono-eng text-[10px] tracking-wider uppercase text-black bg-[#fcc41b] rounded-full px-3 py-1">
          {p.sub}
        </span>
      </div>
      {/* Info */}
      <div className="p-8 flex flex-col gap-5 flex-1">
        <div className="flex items-baseline justify-between gap-4">
          <h3 className="font-display font-bold text-3xl text-white tracking-tight">{p.name}</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-2 font-mono-eng text-[11px] tracking-wider text-zinc-300 border border-[#262626] rounded-full px-3 py-1.5">
            <Wind size={13} className="text-[#fcc41b]" /> {p.flow} m³/hr
          </span>
          <span className="inline-flex items-center gap-2 font-mono-eng text-[11px] tracking-wider text-zinc-300 border border-[#262626] rounded-full px-3 py-1.5">
            <Zap size={13} className="text-[#fcc41b]" /> {p.power}
          </span>
        </div>
        <p className="text-zinc-400 leading-relaxed">{p.tagline}</p>
        <div className="mt-auto flex items-center justify-between gap-4 flex-wrap">
          <span className="flex items-center gap-2 text-[#fcc41b] font-display font-medium text-sm">
            Request Quote
            <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
          </span>
          {p.brochure && (
            <a
              href={p.brochure}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              data-testid={`brochure-link-${p.id}`}
              className="inline-flex items-center gap-2 font-display font-medium text-sm text-zinc-400 border border-[#262626] rounded-full px-4 py-2 hover:text-[#fcc41b] hover:border-[#fcc41b]/40 transition-colors duration-300"
            >
              <FileDown size={15} />
              Brochure
            </a>
          )}
        </div>
      </div>
    </div>
  </Reveal>
);

export const Products = () => {
  return (
    <section id="products" data-testid="product-showcase" className="relative py-28 lg:py-40 bg-[#010101]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <div>
            <Reveal>
              <p className="text-xs tracking-[0.3em] uppercase text-[#fcc41b] font-display font-medium mb-5">
                The Range
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display font-semibold tracking-tighter leading-[0.95] text-white"
                style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.5rem)" }}>
                A ventilator for<br />every roof.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="text-zinc-400 max-w-sm text-base leading-relaxed">
              From compact gable units to industrial-grade fans moving 11,220 m³/hr — all totally solar powered, whisper-quiet and vermin proof.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {PRODUCTS.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
        </div>

        {/* Full spec table */}
        <Reveal>
          <div className="mt-24">
            <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
              <h3 className="font-display font-semibold text-2xl lg:text-3xl text-white tracking-tight">
                Complete range specifications
              </h3>
              <span className="font-mono-eng text-xs text-zinc-500">2025 Series · 9 models</span>
            </div>
            <div className="overflow-x-auto rounded-3xl border border-[#262626]">
              <table className="w-full text-left border-collapse min-w-[720px]" data-testid="spec-table">
                <thead>
                  <tr className="bg-[#0d0d0d] text-zinc-500 font-mono-eng text-[11px] uppercase tracking-wider">
                    <th className="py-4 px-5 font-medium">Model</th>
                    <th className="py-4 px-5 font-medium">Airflow (m³/hr)</th>
                    <th className="py-4 px-5 font-medium">Power</th>
                    <th className="py-4 px-5 font-medium">Panel Placement</th>
                    <th className="py-4 px-5 font-medium">Weight</th>
                    <th className="py-4 px-5 font-medium">PV / Motor</th>
                    <th className="py-4 px-5 font-medium">Range</th>
                  </tr>
                </thead>
                <tbody>
                  {SPECS.map((s) => (
                    <tr key={s.model} className="border-t border-[#1c1c1c] hover:bg-[#0d0d0d] transition-colors duration-200">
                      <td className="py-4 px-5 font-display font-semibold text-white">{s.model}</td>
                      <td className="py-4 px-5 font-mono-eng text-[#fcc41b]">{s.flow}</td>
                      <td className="py-4 px-5 text-zinc-300 font-mono-eng">{s.power}</td>
                      <td className="py-4 px-5 text-zinc-400">{s.placement}</td>
                      <td className="py-4 px-5 text-zinc-400 font-mono-eng">{s.weight}</td>
                      <td className="py-4 px-5 text-zinc-400 font-mono-eng">{s.pv} / {s.motor}</td>
                      <td className="py-4 px-5 text-zinc-400">{s.range}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
